import { useEffect, useState } from "react";
import { api, fmtDate, mediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Upload, Pencil, Trash2, Link2, Search, X } from "lucide-react";
import { refreshTmdbMatches } from "../../components/admin/TmdbMatchBanner";

const EMPTY = {
  type: "Série", title: "", original_title: "", release_date: "", platforms: [],
  overview: "", poster_url: "", genres: "", countries: "", featured: false,
};

const platformLabel = (p, type) => (p === "À venir" ? (type === "Film" ? "Au cinéma" : "Plateforme à confirmer") : p);

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-[11px] text-neutral-400">{hint}</span>}
  </label>
);

const toPayload = (f) => ({
  ...f,
  genres: f.genres.split(",").map((s) => s.trim()).filter(Boolean),
  countries: f.countries.split(",").map((s) => s.trim()).filter(Boolean),
});

const fromDoc = (d) => ({
  ...EMPTY, ...d,
  genres: (d.genres || []).join(", "), countries: (d.countries || []).join(", "),
  poster_url: d.poster_url || "", original_title: d.original_title || "", overview: d.overview || "",
});

const ReleaseForm = ({ initial, platforms, onSaved, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const editing = !!initial.tmdb_id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const togglePlatform = (p) =>
    setF({ ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Envoi de l'affiche…");
    try {
      const { data } = await api.post("/media/upload", fd);
      setF((cur) => ({ ...cur, poster_url: data.url }));
      toast.success("Affiche ajoutée", { id: t });
    } catch {
      toast.error("Échec de l'envoi", { id: t });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.poster_url) return toast.error("L'affiche est obligatoire");
    if (!f.platforms.length) return toast.error("Choisissez au moins une plateforme");
    setBusy(true);
    try {
      const payload = toPayload(f);
      if (editing) await api.put(`/admin/manual-releases/${initial.tmdb_id}`, payload);
      else await api.post("/admin/manual-releases", payload);
      toast.success(editing ? "Fiche mise à jour" : "Sortie ajoutée au site");
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Champs invalides" : d || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="manual-release-form" className="bg-white border border-[var(--line)] rounded-md p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl">{editing ? `Modifier « ${initial.title} »` : "Nouvelle sortie manuelle"}</h2>
        <button type="button" onClick={onCancel} aria-label="Fermer" className="text-neutral-400 hover:text-black"><X className="h-5 w-5" /></button>
      </div>
      <div className="grid md:grid-cols-[180px_1fr] gap-6">
        <div>
          <div className="aspect-[2/3] bg-neutral-100 rounded border border-[var(--line)] overflow-hidden grid place-items-center">
            {f.poster_url ? <img src={mediaUrl(f.poster_url)} alt="" className="w-full h-full object-cover" data-testid="manual-poster-preview" /> : <span className="text-xs text-neutral-400">Affiche 2:3</span>}
          </div>
          <label className="btn-ghost mt-3 cursor-pointer w-full justify-center">
            <Upload className="h-4 w-4" /> Affiche *
            <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="manual-poster-input" />
          </label>
        </div>
        <div className="space-y-4">
          <div className="flex gap-2" role="radiogroup">
            {["Série", "Film"].map((t) => (
              <button key={t} type="button" disabled={editing} onClick={() => setF({ ...f, type: t })} data-testid={`manual-type-${t}`}
                className={`px-4 py-1.5 text-xs uppercase tracking-[0.2em] rounded-full border transition-colors ${f.type === t ? "bg-black text-white border-black" : "border-[var(--line-strong)] text-neutral-600"} disabled:opacity-60`}>
                {t}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Titre *"><input required className="input" value={f.title} onChange={set("title")} data-testid="manual-title" /></Field>
            <Field label="Titre original"><input className="input" value={f.original_title} onChange={set("original_title")} data-testid="manual-original-title" /></Field>
            <Field label="Date de sortie / 1re diffusion *"><input required type="date" className="input" value={f.release_date} onChange={set("release_date")} data-testid="manual-date" /></Field>
            <Field label="Mise en avant">
              <label className="flex items-center gap-2 text-sm h-10">
                <input type="checkbox" checked={f.featured} onChange={set("featured")} className="h-4 w-4 accent-[var(--red)]" data-testid="manual-featured" />
                Remonter dans les listes et la newsletter
              </label>
            </Field>
          </div>
          <Field label="Plateforme(s) *">
            <div className="flex flex-wrap gap-2">
              {platforms.map((p) => (
                <button key={p} type="button" onClick={() => togglePlatform(p)} data-testid={`manual-platform-${p}`}
                  className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${f.platforms.includes(p) ? "bg-[var(--red)] text-white border-[var(--red)]" : "border-[var(--line-strong)] text-neutral-700 hover:border-black"}`}>
                  {platformLabel(p, f.type)}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Synopsis"><textarea className="input min-h-[110px]" value={f.overview} onChange={set("overview")} data-testid="manual-overview" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Genres" hint="Séparés par des virgules (ex. Drame, Thriller)"><input className="input" value={f.genres} onChange={set("genres")} data-testid="manual-genres" /></Field>
            <Field label="Pays" hint="Ex. France"><input className="input" value={f.countries} onChange={set("countries")} data-testid="manual-countries" /></Field>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-ghost">Annuler</button>
        <button type="submit" disabled={busy} className="btn-red" data-testid="manual-save-btn">{editing ? "Enregistrer" : "Publier sur le site"}</button>
      </div>
    </form>
  );
};

const LinkPanel = ({ item, onDone, onCancel }) => {
  const [q, setQ] = useState(item.original_title || item.title);
  const [results, setResults] = useState(null);
  const search = async (e) => {
    e?.preventDefault();
    setResults(null);
    try {
      const { data } = await api.get("/admin/manual-releases/tmdb-search", { params: { q, type: item.type } });
      setResults(data.results);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Recherche impossible");
      setResults([]);
    }
  };
  useEffect(() => { search(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const link = async (r) => {
    if (!window.confirm(`Relier « ${item.title} » à la fiche TMDB « ${r.title} » (${r.date || "date inconnue"}) ? Les votes sont conservés.`)) return;
    try {
      const { data } = await api.post(`/admin/manual-releases/${item.tmdb_id}/link`, { tmdb_id: r.tmdb_id });
      toast.success(data.action === "merged" ? "Fusionnée avec la fiche déjà importée" : "Reliée à TMDB — affiche et synopsis officiels récupérés");
      refreshTmdbMatches();
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Liaison impossible");
    }
  };
  return (
    <div className="bg-[var(--bg-cream)] border border-[var(--line)] rounded-md p-4 mt-3" data-testid={`manual-link-panel-${item.tmdb_id}`}>
      <form onSubmit={search} className="flex gap-2">
        <input className="input flex-1" value={q} onChange={(e) => setQ(e.target.value)} data-testid="manual-link-search-input" />
        <button className="btn-ghost" type="submit"><Search className="h-4 w-4" /> Chercher</button>
        <button className="btn-ghost" type="button" onClick={onCancel}>Fermer</button>
      </form>
      {results === null ? (
        <div className="text-sm text-neutral-500 mt-3">Recherche sur TMDB…</div>
      ) : results.length === 0 ? (
        <div className="text-sm text-neutral-500 mt-3">Aucune fiche TMDB trouvée pour l'instant.</div>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--line)]">
          {results.map((r) => (
            <li key={r.tmdb_id} className="py-2 flex items-center gap-3">
              {r.poster_url ? <img src={r.poster_url} alt="" className="h-14 w-10 object-cover rounded-sm" /> : <div className="h-14 w-10 bg-neutral-200 rounded-sm" />}
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-medium truncate">{r.title}</div>
                <div className="text-xs text-neutral-500">{r.original_title} · {r.date || "date inconnue"} · TMDB {r.tmdb_id}{r.in_catalogue ? " · déjà au catalogue" : ""}</div>
              </div>
              <button className="btn-red" onClick={() => link(r)} data-testid={`manual-link-choose-${r.tmdb_id}`}>Relier</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AdminManualReleases = () => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [linking, setLinking] = useState(null);

  const load = () => api.get("/admin/manual-releases").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    window.addEventListener("rp:manual-releases-changed", load);
    return () => window.removeEventListener("rp:manual-releases-changed", load);
  }, []);
  const [checking, setChecking] = useState(false);
  const checkNow = async () => {
    setChecking(true);
    try {
      const { data: r } = await api.post("/admin/manual-releases/check-matches");
      toast.success(r.new ? `${r.new} nouvelle(s) fiche(s) TMDB trouvée(s)` : "Aucune nouvelle fiche TMDB pour l'instant");
      refreshTmdbMatches();
    } catch {
      toast.error("Vérification impossible");
    } finally {
      setChecking(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Supprimer « ${it.title} » du site ? Les votes associés seront supprimés.`)) return;
    await api.delete(`/admin/manual-releases/${it.tmdb_id}`);
    toast.success("Sortie supprimée");
    load();
  };

  if (!data) return <div className="text-sm text-neutral-500">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="admin-manual-releases-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)]">Catalogue</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Sorties manuelles</h1>
          <p className="text-neutral-600 mt-2 text-sm max-w-2xl">
            Pour les titres absents de TMDB. Ils apparaissent partout sur le site et dans la newsletter, et la synchronisation ne les modifie jamais.
            Quand la fiche TMDB existe, cliquez sur « Relier à TMDB » pour récupérer l'affiche et le synopsis officiels.
          </p>
        </div>
        {!editing && (
          <div className="flex flex-wrap gap-3">
            <button className="btn-ghost" onClick={checkNow} disabled={checking} data-testid="manual-check-matches-btn">
              <Search className="h-4 w-4" /> {checking ? "Vérification…" : "Vérifier sur TMDB"}
            </button>
            <button className="btn-red" onClick={() => setEditing({ ...EMPTY })} data-testid="manual-new-btn"><Plus className="h-4 w-4" /> Nouvelle sortie</button>
          </div>
        )}
      </div>

      {editing && (
        <ReleaseForm initial={editing} platforms={data.platforms} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <section className="bg-white border border-[var(--line)] rounded-md p-6" data-testid="manual-list">
        {data.items.length === 0 ? (
          <div className="text-sm text-neutral-500">Aucune sortie manuelle pour l'instant.</div>
        ) : (
          <ul className="divide-y divide-[var(--line)]">
            {data.items.map((it) => (
              <li key={it.tmdb_id} className="py-4" data-testid={`manual-item-${it.tmdb_id}`}>
                <div className="flex items-center gap-4">
                  <img src={mediaUrl(it.poster_url)} alt="" className="h-20 w-14 object-cover rounded-sm bg-neutral-100" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg truncate">{it.title}</div>
                    <div className="text-xs text-neutral-500">
                      {it.type} · {fmtDate(it.release_date)} · {it.platforms.map((p) => platformLabel(p, it.type)).join(", ")}{it.featured ? " · mise en avant" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button className="btn-ghost" onClick={() => setLinking(linking === it.tmdb_id ? null : it.tmdb_id)} data-testid={`manual-link-btn-${it.tmdb_id}`}><Link2 className="h-4 w-4" /> Relier à TMDB</button>
                    <button className="btn-ghost" onClick={() => { setEditing(fromDoc(it)); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="Modifier" data-testid={`manual-edit-btn-${it.tmdb_id}`}><Pencil className="h-4 w-4" /></button>
                    <button className="btn-ghost" onClick={() => remove(it)} aria-label="Supprimer" data-testid={`manual-delete-btn-${it.tmdb_id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                {linking === it.tmdb_id && <LinkPanel item={it} onCancel={() => setLinking(null)} onDone={() => { setLinking(null); load(); }} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
